import Vue from 'vue'

import api from '@/api'
import { comparadorString } from '@/helpers/FunctionHelper'
import DateService from '@/helpers/DateService'
import {
  defineSyncSchema,
  createSyncState,
  createSyncRequest,
  recordWrite,
  mergeSyncResponse,
  schemaKinds,
  syncMutations
} from '@/helpers/storeSync'

const RECURSO_SYNC_SCHEMA = defineSyncSchema({ key: 'ID_RECURSO', kind: 'recurso', insertAt: 'end' })

const initialState = {
  recursos: [],
  posSinRecursos: [],
  recursoEstadoTipos: [],
  estados: [],
  fechaInicioShow: true,
  sync: createSyncState(schemaKinds(RECURSO_SYNC_SCHEMA))
}

const getters = {
  recursos: (state) => {
    return state.recursos
  },

  getRecursosActivos: (state) => {
    return state.recursos.filter(x => x.DESPACHABLE && x.ACTIVO && !x.BORRADO && x.RECURSO_SECTOR_ESTADO[0]?.RECURSO_SECTOR_ESTADO_TIPO.NOMBRE === 'Desasignado')
  },

  recursoEstadoTipos: state => {
    return [...state.recursoEstadoTipos].sort((a, b) => comparadorString.compare(a.NOMBRE, b.NOMBRE))
  },

  recursoEstadoTipoById: (state) => (idRecursoEstadoTipo) => {
    return state.recursoEstadoTipos.find(recursoEstadoTipo => recursoEstadoTipo.ID_RECURSO_SECTOR_ESTADO_TIPO.equalsIgnoreCase(idRecursoEstadoTipo))
  },


  estados: (state) => state.estados,

  desasignado: state => {
    return state.recursoEstadoTipos.reduce((max, obj) => {
      return (obj.ORDEN > max.ORDEN) ? obj : max
    })
  },

  posSinRecursos: (state) => state.posSinRecursos,

  getRecursoByID: (state) => (idRecurso) => {
    if (!idRecurso) return null

    return state.recursos.find(x => x.ID_RECURSO.equalsIgnoreCase(idRecurso))
  },

  medios: state => {
    return state.recursos.filter(x => x.MEDIO)
  },

  personales: state => {
    return state.recursos.filter(x => x.PERSONAL)
  },

  getRecursoNameById: (state) => (idRecurso, formato = 'apellidos-nombre') => {
    const recurso = state.recursos.find(r => r.ID_RECURSO === idRecurso)
    if (!recurso) return '-'

    // Si es medio, devolver directamente
    if (recurso.MEDIO?.MEDIO) return recurso.MEDIO.MEDIO

    const nombre = recurso.PERSONAL?.NOMBRE ?? '-'
    const apellidos = recurso.PERSONAL?.APELLIDOS ?? ''

    switch (formato) {
      case 'apellidos-nombre':
        return apellidos ? `${apellidos}, ${nombre}` : nombre
      case 'nombre-apellidos':
      default:
        return `${nombre} ${apellidos}`.trim()
    }
  },

  getMedioById: (state) => (idMedio) => {
    let recurso = null

    if (idMedio) {
      recurso = state.recursos.find(x => x.MEDIO?.ID_MEDIO.equalsIgnoreCase(idMedio))
    }
    return recurso
  },

  getPersonalById: (state) => (idPersonal) => {
    let recurso = null

    if (idPersonal) {
      recurso = state.recursos.find(x => x.PERSONAL?.ID_PERSONAL.equalsIgnoreCase(idPersonal))
    }
    return recurso
  },

  getRecursoByPersonalAsociado: (state) => (idPersonal) => {
    let recurso = null

    if (idPersonal) {
      recurso = state.recursos.find(x => x.MEDIO?.ID_PERSONAL?.equalsIgnoreCase(idPersonal))
    }
    return recurso
  },

  getRecursoNombreCompleto: (state) => (recurso) => {
    return `${recurso.PERSONAL.APELLIDOS}, ${recurso.PERSONAL.NOMBRE}`
  },

  getRecursoEstadoTipoByEmergenciaTipo: (state, localGetters, rootState, rootGetters) => (idEmergenciaTipo) => {
    if (!idEmergenciaTipo) return []
    const emergenciasClase = rootGetters['emergencia/emergenciasClase']
    const estados = state.recursoEstadoTipos

    // Buscar clase y categoría que contienen el tipo
    const tipoInfo = emergenciasClase.flatMap(clase =>
      (clase.CATEGORIA || []).flatMap(categoria =>
        (categoria.TIPO || []).map(tipo => ({
          tipoId: tipo.ID_EMERGENCIA_TIPO,
          claseId: clase.ID_EMERGENCIA_CLASE,
          categoriaId: categoria.ID_EMERGENCIA_CATEGORIA
        }))
      )
    ).find(t => t.tipoId === idEmergenciaTipo)

    if (!tipoInfo) return []

    const { claseId, categoriaId } = tipoInfo

    // Filtrar estados con asignaciones relevantes
    const estadosAsignados = estados.filter(estado => {
      return (estado.RECURSO_SECTOR_ESTADO_TIPO_ASIGNACION || []).some(asignacion =>
        asignacion.ID_EMERGENCIA_TIPO === idEmergenciaTipo ||
        asignacion.ID_EMERGENCIA_CATEGORIA === categoriaId ||
        asignacion.ID_EMERGENCIA_CLASE === claseId
      )
    })

    // Ordenar por ORDEN
    return estadosAsignados.sort((a, b) => a.ORDEN - b.ORDEN)
  },

  recursoImagenes: (state) => state.recursoImagenes,

  fechaInicioShow: (state) => state.fechaInicioShow,

  getPersonalName: (state) => (id) => {
    const recurso = state.recursos.find(r => r.PERSONAL?.ID_PERSONAL?.equalsIgnoreCase(id))
    if (!recurso) return ''
    return `${recurso.PERSONAL?.NOMBRE || ''} ${recurso.PERSONAL?.APELLIDOS || ''}`.trim()
  },

  getRecursoNombreGeofencing: (state, getters, _rootState, rootGetters) => (
    idRecurso,
    fecha = DateService.getCurrentDayJs()
  ) => {
    const recurso = getters.getRecursoByID(idRecurso)

    if (!recurso) {
      return {
        NOMBRE: '',
        ID_MEDIO: null,
        ID_PERSONAL: null,
      }
    }

    // Un dispositivo asignado al recurso de personal debe mostrarse
    // como personal, sin añadirle el medio en el que pueda estar de guardia.
    if (recurso.PERSONAL) {
      return {
        NOMBRE: getters.getRecursoNameById(recurso.ID_RECURSO, 'nombre-apellidos'),
        ID_MEDIO: null,
        ID_PERSONAL: recurso.PERSONAL.ID_PERSONAL,
      }
    }

    if (recurso.MEDIO) {
      const idMedio = recurso.MEDIO.ID_MEDIO
      const medioPersonal = rootGetters['medioPersonal/getPersonalAsignado'](
        idMedio,
        fecha
      )

      const idPersonal = medioPersonal?.ID_PERSONAL ?? null
      // recurso ya esta resuelto: leer su nombre directamente evita un segundo
      // recorrido de state.recursos por cada fila listada.
      const nombreMedio = recurso.MEDIO.MEDIO || getters.getRecursoNameById(recurso.ID_RECURSO)
      const nombrePersonal = idPersonal
        ? getters.getPersonalName(idPersonal)
        : ''

      return {
        NOMBRE: nombrePersonal
          ? `${nombreMedio} (${nombrePersonal})`
          : nombreMedio,
        ID_MEDIO: idMedio,
        ID_PERSONAL: idPersonal,
      }
    }

    return {
      NOMBRE: getters.getRecursoNameById(recurso.ID_RECURSO),
      ID_MEDIO: null,
      ID_PERSONAL: null,
    }
  },
}

const mutations = {
  ...syncMutations,

  SET_POS_SIN_RECURSOS(state, posSinRecursos) {
    state.posSinRecursos = posSinRecursos
  },

  SET_ESTADOS(state, estados) {
    state.estados = estados
  },

  MERGE_RECURSOS(state, { rows, request }) {
    state.recursos = mergeSyncResponse(state.sync, state.recursos, rows, RECURSO_SYNC_SCHEMA, request)
  },

  SET_RECURSO_ESTADO_TIPOS(state, recursoEstadoTipos) {
    state.recursoEstadoTipos = recursoEstadoTipos.sort((a, b) => a.ORDEN - b.ORDEN)
  },

  SET_DISPOSITIVOS_RECURSOS(state, dispositivos) {
    const posMap = new Map(state.recursos.map((r, i) => [r.ID_RECURSO, i]))

    const updates = new Map()
    dispositivos.forEach(d => {
      if (!d.GEO_RECURSO_DISPOSITIVO) return
      const pos = posMap.get(d.GEO_RECURSO_DISPOSITIVO.ID_RECURSO)
      if (pos === undefined) return
      updates.set(pos, { ...d.GEO_RECURSO_DISPOSITIVO, GEO_DISPOSITIVO: d })
    })

    if (updates.size === 0) return

    state.recursos = state.recursos.map((r, i) => {
      const geoDispositivo = updates.get(i)
      return geoDispositivo ? { ...r, GEO_RECURSO_DISPOSITIVO: geoDispositivo } : r
    })
  },

  SET_FECHA_INICIO(state, fechaInicio) {
    state.fechaInicioShow = fechaInicio
  },

  SOCKET_addRecurso(state, data) {

    if (data.ID_GEO_DISPOSITIVO) {
      const dispositivo = this.getters['dispositivo/getDispositivoByID'](data.ID_GEO_DISPOSITIVO)

      if (dispositivo) {
        data.DISPOSITIVO = dispositivo
      }
    } else {
      data.DISPOSITIVO = null
    }

    if (data.GEO_RECURSO_DISPOSITIVO) {
      const nuevoDispositivoId = data.GEO_RECURSO_DISPOSITIVO.ID_GEO_DISPOSITIVO

      state.recursos.forEach(r => {
        if (r.ID_RECURSO !== data.ID_RECURSO && r.GEO_RECURSO_DISPOSITIVO?.ID_GEO_DISPOSITIVO === nuevoDispositivoId) {
          r.GEO_RECURSO_DISPOSITIVO = null
          recordWrite(state.sync, 'recurso', r.ID_RECURSO)
        }
      })
    }

    state.recursos.push(data)
    recordWrite(state.sync, 'recurso', data.ID_RECURSO)

    const nombreRecurso = data.ALIAS ||
      data.MEDIO?.MEDIO ||
      `${data.PERSONAL?.NOMBRE || ''} ${data.PERSONAL?.APELLIDOS || ''}`.trim()

    this.notify('Nuevo recurso', `Se ha añadido el recursos ${nombreRecurso}`)
  },

  SOCKET_updateRecurso(state, data) {
    const { ID_RECURSO, GEO_RECURSO_DISPOSITIVO, ALIAS, MEDIO, PERSONAL } = data

    const pos = state.recursos.findIndex(
      r => r.ID_RECURSO?.toString() === ID_RECURSO?.toString()
    )

    if (pos === -1) return

    if (GEO_RECURSO_DISPOSITIVO?.ID_GEO_DISPOSITIVO) {
      const id = GEO_RECURSO_DISPOSITIVO.ID_GEO_DISPOSITIVO

      state.recursos.forEach(r => {
        if (r.ID_RECURSO !== ID_RECURSO && r.GEO_RECURSO_DISPOSITIVO?.ID_GEO_DISPOSITIVO === id) {
          Vue.set(r, 'GEO_RECURSO_DISPOSITIVO', null)
          recordWrite(state.sync, 'recurso', r.ID_RECURSO)
        }
      })
    }

    const recursoActual = state.recursos[pos]
    const recursoActualizado = { ...recursoActual }

    Object.keys(data).forEach(key => {
      if (key !== 'GEO_RECURSO_DISPOSITIVO') {
        recursoActualizado[key] = data[key]
      }
    })

    if (data.GEO_RECURSO_DISPOSITIVO?.ID_GEO_DISPOSITIVO) {
      const dispositivo = this.getters['dispositivo/getDispositivoByID'](data.GEO_RECURSO_DISPOSITIVO.ID_GEO_DISPOSITIVO)

      if (dispositivo) {
        recursoActualizado.GEO_RECURSO_DISPOSITIVO = {
          ...recursoActualizado.GEO_RECURSO_DISPOSITIVO,
          ID_GEO_DISPOSITIVO: dispositivo.ID_GEO_DISPOSITIVO,
          GEO_DISPOSITIVO: dispositivo
        }
      }
    } else {
      recursoActualizado.GEO_RECURSO_DISPOSITIVO = null
    }

    Vue.set(state.recursos, pos, recursoActualizado)
    recordWrite(state.sync, 'recurso', ID_RECURSO)

    const nombreRecurso =
      ALIAS || MEDIO?.MEDIO || [PERSONAL?.NOMBRE, PERSONAL?.APELLIDOS].filter(Boolean).join(' ')

    this.notify('Recurso actualizado', `Se ha actualizado el recurso ${nombreRecurso}`)
  },

  SOCKET_deleteRecurso(state, data) {
    const positionRecurso = state.recursos.findIndex(x => x.ID_RECURSO.equalsIgnoreCase(data))
    recordWrite(state.sync, 'recurso', data, true)
    if (positionRecurso !== -1) {
      const recursoBorrado = state.recursos[positionRecurso]
      state.recursos.splice(positionRecurso, 1)
      const nombreRecurso = recursoBorrado.ALIAS ||
        recursoBorrado.MEDIO?.MEDIO ||
        `${recursoBorrado.PERSONAL?.NOMBRE || ''} ${recursoBorrado.PERSONAL?.APELLIDOS || ''}`.trim()

      this.notify('Recurso eliminado', `Se ha eliminado el recurso ${nombreRecurso}`)
    }
  },

  SOCKET_updateRecursoEstado(state, data) {
    const positionRecurso = state.recursos.findIndex(x => x.ID_RECURSO.equalsIgnoreCase(data.ID_RECURSO))
    if (positionRecurso !== -1) {
      if (data.ID_RECURSO_ESTADO) {
        state.recursos[positionRecurso].ID_RECURSO_ESTADO = data.ID_RECURSO_ESTADO
      }
      if (data.RECURSO_SECTOR_ESTADO) {
        state.recursos[positionRecurso].RECURSO_SECTOR_ESTADO = data.RECURSO_SECTOR_ESTADO
      } else {
        state.recursos[positionRecurso].RECURSO_SECTOR_ESTADO = data
      }

      recordWrite(state.sync, 'recurso', data.ID_RECURSO)

      const nombreRecurso = data.ALIAS ||
        data.MEDIO?.MEDIO ||
        `${data.PERSONAL?.NOMBRE || ''} ${data.PERSONAL?.APELLIDOS || ''}`.trim()

      this.notify('Recurso actualizado', `Se ha actualizado el recurso ${nombreRecurso}`)
    }
  },

  SOCKET_updateRecursoSectorEstado(state, data) {
    data.forEach(recursoSector => {
      const positionRecurso = state.recursos.findIndex(r => r.ID_RECURSO.equalsIgnoreCase(recursoSector.ID_RECURSO))
      if (positionRecurso !== -1) {
        recordWrite(state.sync, 'recurso', recursoSector.ID_RECURSO)
        delete recursoSector.ID_RECURSO
        state.recursos[positionRecurso].RECURSO_SECTOR_ESTADO = recursoSector.RECURSO_SECTOR_ESTADO
        const nombreRecurso =
          state.recursos[positionRecurso].ALIAS ||
          state.recursos[positionRecurso].MEDIO?.MEDIO ||
          `${state.recursos[positionRecurso].PERSONAL?.NOMBRE || ''} ${state.recursos[positionRecurso].PERSONAL?.APELLIDOS || ''}`.trim()

        this.notify('Recurso actualizado', `Se ha actualizado el recurso ${nombreRecurso}`)
      }
    })
  },

  SOCKET_addImagenRecurso(state, data) {
    const recurso = state.recursos.find(r => r.ID_RECURSO === data)

    if (recurso) {
      recurso.NUM_IMAGENES++
      recordWrite(state.sync, 'recurso', data)
    }
  },

  SOCKET_deleteImagenRecurso(state, data) {
    const recurso = state.recursos.find(r => r.ID_RECURSO === data)

    if (recurso && recurso.NUM_IMAGENES > 0) {
      recurso.NUM_IMAGENES--
      recordWrite(state.sync, 'recurso', data)
    }
  }
}

const actions = {
  async fetchRecursos({ commit, state, rootGetters }) {
    try {
      commit('BEGIN_SYNC_REQUEST')
      const request = createSyncRequest(state.sync.seq)

      const response = await api.recurso.getRecursos()

      if (response.status === 200) {
        const dispositivosAsignados = rootGetters['dispositivo/dispositivosAsignados']
        const relacionesMap = {}
        dispositivosAsignados.forEach(d => {
          if (d.GEO_RECURSO_DISPOSITIVO) {
            relacionesMap[d.GEO_RECURSO_DISPOSITIVO.ID_RECURSO] = {
              ...d.GEO_RECURSO_DISPOSITIVO,
              GEO_DISPOSITIVO: d
            }
          }
        })

        const recursos = response.data.map(r => ({
          ...r,
          GEO_RECURSO_DISPOSITIVO: relacionesMap[r.ID_RECURSO] ?? r.GEO_RECURSO_DISPOSITIVO ?? null
        }))

        commit('MERGE_RECURSOS', { rows: recursos, request })
      }
    } catch (err) {
      console.error(err)
    }
  },

  // Acción que obtiene los estados desde la API
  async fetchEstadosRecurso({ commit }) {
    try {
      const response = await api.recurso.getEstadosRecurso()
      commit('SET_ESTADOS', response.data)
    } catch (err) {
      console.error('Error al obtener los estados:', err)
      throw err
    }
  },

  async fetchHistoricoRecursoDespacho({ commit }, idRecurso) {
    try {
      const response = await api.recurso.getHistoricoRecursoDespacho(idRecurso)
      return response.data
    } catch (err) {
      console.error('Error al obtener el historico de despacho:', err)
      throw err
    }
  },

  async addRecurso({ commit }, data) {
    try {
      const response = await api.recurso.addRecurso(data)

      if (response.status === 200) {
        // commit('SOCKET_addRecurso', data)
      }
    } catch (err) {
      this.notify('Error al editar recurso', 'error')
      console.error(err)
    }
  },

  async updateRecurso({ commit }, data) {
    try {
      await api.recurso.updateRecurso(data)
    } catch (err) {
      this.notify('Error al editar recurso', 'error')
      console.error(err)
    }
  },
  async deleteRecurso({ commit }, data) {
    try {
      const response = await api.recurso.deleteRecurso(data)

      if (response.status === 200) {
        commit('SOCKET_deleteRecurso', data)
      }
    } catch (err) {
      console.error(err)
    }
  },

  async updateRecursoEstado({ commit }, data) {
    try {
      const response = await api.recurso.updateRecursoEstado(data)

      if (response.status === 200) {
        // commit('SOCKET_updateRecursoEstado', data)
      }
    } catch (err) {
      this.notify('Error al editar recurso', 'error')
      console.error(err)
    }
  },

  updateFechaInicio({ commit }, data) {
    try {
      commit('SET_FECHA_INICIO', data)
    } catch (error) {
      console.error(error)
      throw error
    }
  },

  async fetchRecursoEstadoTipos({ commit }) {
    try {
      const response = await api.recurso.getRecursoEstadoTipos()

      if (response.status === 200) {
        commit('SET_RECURSO_ESTADO_TIPOS', response.data)
      }
    } catch (err) {
      console.error(err)
      throw err
    }
  },

  setDispositivosRecursos({ commit }, dispositivos) {
    try {
      commit('SET_DISPOSITIVOS_RECURSOS', dispositivos)
    } catch (err) {
      console.error(err)
      throw err
    }
  },

  async getDispositivos({ _commit }, idRecurso, dateInit) {
    try {
      return await api.recurso.getDispositivos(idRecurso, dateInit)
    } catch (err) {
      console.error('[recurso] Error al obtener dispositivos de un recurso:', err)
      throw err
    }
  },
  async getTrackFechasRecurso({ _commit }, data) {
    try {
      return await api.recurso.getTrackFechasRecurso(data)
    } catch (err) {
      console.error('[recurso] Error al obtener el track de recursos:', err)
      throw err
    }
  }
}

const module = {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions
}

export default module
